import React, { useState, useEffect } from 'react';
import DataService from '../../services/data.service';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { Edit2 } from 'lucide-react';

const Header = ({ onToggleSidebar }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [salonHours, setSalonHours] = useState({ startTime: '10:00', endTime: '19:00' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editHours, setEditHours] = useState({ startTime: '10:00', endTime: '19:00' });
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const logout = () => {
    DataService.deleteAccessToken();
    navigate('/login');
  };

  const closeMenu = () => setIsMobileMenuOpen(false);

  const fetchSalonHours = async () => {
    try {
      const response = await DataService.getSalonHours();
      console.log('Salon hours API response:', response);

      // Handle different response structures
      let hours = null;
      if (response.data) {
        // Check if data is directly in response.data
        if (response.data.startTime && response.data.endTime) {
          hours = response.data;
        }
        // Check if data is nested (e.g., response.data.data)
        else if (response.data.data && response.data.data.startTime && response.data.data.endTime) {
          hours = response.data.data;
        }
        // Check for alternative property names (start_time, end_time)
        else if (response.data.start_time && response.data.end_time) {
          hours = {
            startTime: response.data.start_time,
            endTime: response.data.end_time
          };
        }
        else if (response.data.data && response.data.data.start_time && response.data.data.end_time) {
          hours = {
            startTime: response.data.data.start_time,
            endTime: response.data.data.end_time
          };
        }
      }

      if (hours) {
        console.log('Setting salon hours:', hours);
        setSalonHours(hours);
      } else {
        console.warn('Salon hours not found in API response, using defaults');
      }
    } catch (error) {
      console.error('Failed to fetch salon hours:', error);
      console.log('Using default salon hours: 10:00 - 19:00');
    }
  };

  const handleSaveHours = async () => {
    try {
      const response = await DataService.updateSalonHours(editHours);
      console.log('Save hours response:', response);
      setSalonHours(editHours);
      setShowEditModal(false);
      toast.success('Salon hours updated successfully!');
    } catch (error) {
      console.error('Failed to update salon hours:', error);
      toast.error('Failed to update salon hours: ' + (error.response?.data?.message || error.message));
    }
  };

  const openEditModal = () => {
    setEditHours(salonHours);
    setShowEditModal(true);
  };

  const formatTime = (time) => {
    const [h, m] = time.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const generate15MinuteSlots = () => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    return slots;
  };

  const timeSlots = generate15MinuteSlots();

  useEffect(() => {
    fetchSalonHours();
  }, []);

  return (
    <>
    <nav className="navbar navbar-dark  px-3" style={{background:'#1c2833'}}>
      <div className="d-flex w-100 align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          {/* Sidebar Toggle Icon */}


          {/* Logo */}
          <a className="navbar-brand d-flex align-items-center mb-0" href="/" style={{ gap: '0.5rem' }}>
            <img
              src="/images/Logo_salon.svg"
              alt="Divine Blush Beauty Logo"
              height="36"
              className="d-inline-block align-middle"
              style={{ objectFit: 'contain',filter: 'brightness(0) invert(1)' }}
            />
            <span
              className="d-none d-sm-inline"
              style={{ color:'white', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
            >
              Dashboard
            </span>
          </a>
           <button className="btn btn-outline-light me-3 new-sidebar" onClick={onToggleSidebar}>
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>

        {/* Salon Hours */}
        <div className="d-none d-md-flex align-items-center text-white" style={{ fontSize: '0.9rem' }}>
          <span style={{ marginRight: '0.5rem' }}>
            {formatTime(salonHours.startTime)} - {formatTime(salonHours.endTime)}
          </span>
          <Edit2
            size={16}
            onClick={openEditModal}
            style={{ cursor: 'pointer', opacity: 0.8 }}
          />
        </div>

        {/* Mobile menu toggle (for small screens) */}
        <button className="btn btn-outline-light d-md-none" onClick={toggleMobileMenu}>
          ☰
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {isMobileMenuOpen && (
        <div className=" mt-2 px-2 py-2" style={{ zIndex: 1000 }}>
          <ul className="nav flex-column">
            {[
              { path: '/dashboard', label: 'Dashboard' },
                { path: '/users', label: 'Users' },
              { path: '/bookings', label: 'All Bookings' },
              { path: '/open-bookings', label: 'Open Bookings' },
              { path: '/confirmed-bookings', label: 'Confirmed Bookings' },
              { path: '/booking-questions', label: 'Booking Questions' },
              { path: '/categories', label: 'Categories' },
              { path: '/sub-categories', label: 'Sub Categories' },
              { path: '/block-dates', label: 'Block Dates' },
              { path: '/page-settings', label: 'Page Settings' },
              { path: '/seo-pages', label: 'SEO Pages' },
              { path: '/email-settings', label: 'Email Settings' },
              { path: '/customer-images', label: 'Customer Images' },
              { path: '/reviewsListing', label: 'Reviews' },
              { path: '/promotions', label: 'Promotions' },
              { path: '/change-password', label: 'Change Password' },
            ].map(({ path, label }) => (
              <li className="nav-item" key={path}>
                <Link to={path} className="nav-link text-white py-1" onClick={closeMenu}>
                  {label}
                </Link>
              </li>
            ))}
            <li className="nav-item">
              <button
                onClick={logout}
                className="nav-link text-white text-start bg-transparent border-0"
                style={{ cursor: 'pointer' }}
              >
                Logout
              </button>
            </li>
          </ul>
        </div>
      )}
    </nav>

    {/* Edit Hours Modal */}
    {showEditModal && (
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Salon Hours</h5>
              <button type="button" className="close" onClick={() => setShowEditModal(false)}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Start Time</label>
                <select
                  className="form-control"
                  value={editHours.startTime}
                  onChange={(e) => setEditHours({ ...editHours, startTime: e.target.value })}
                >
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {formatTime(slot)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>End Time</label>
                <select
                  className="form-control"
                  value={editHours.endTime}
                  onChange={(e) => setEditHours({ ...editHours, endTime: e.target.value })}
                >
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {formatTime(slot)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveHours}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Header;
